/**
 * MaintenanceSettings – Einmalige Wartungs-Aktionen (Admin)
 *
 * 1) Normalisierung von Termin-Zeitstempeln auf Schweizer Lokalzeit.
 * 2) Reparatur von Credit-Abos (10er-Abos), deren Restpunkte durch einen
 *    früheren Verkaufs-Bug auf 0 standen (Anzeige "0/10" statt "10/10").
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Clock, Search, Wand2, AlertTriangle, CheckCircle, Ticket, Coins, RotateCcw, FileWarning } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENDPOINT = `${API}/admin/maintenance/normalize-appointment-timezones`;
const ABO_ENDPOINT = `${API}/admin/maintenance/repair-credit-abos`;
const POINTS_ENDPOINT = `${API}/admin/maintenance/repair-points-packages`;
const ZERO_LIST_ENDPOINT = `${API}/admin/maintenance/zero-billed-appointments`;
const ZERO_RESET_ENDPOINT = `${API}/admin/maintenance/reset-zero-billed`;

const fmtDateTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const TimezoneCard = () => {
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const runDry = async () => {
    setChecking(true);
    setLastResult(null);
    try {
      const res = await axios.post(
        `${ENDPOINT}?dry_run=true&include_naive=true`,
        {},
        { headers: authHeaders() }
      );
      setPreview(res.data);
      if ((res.data?.to_change || 0) === 0) {
        toast.success('Alles sauber – keine Termine müssen umgestellt werden.');
      } else {
        toast.info(`${res.data.to_change} Termine würden normalisiert.`);
      }
    } catch (e) {
      toast.error(`Prüfung fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    } finally {
      setChecking(false);
    }
  };

  const runReal = async () => {
    setShowConfirm(false);
    setRunning(true);
    try {
      const res = await axios.post(
        `${ENDPOINT}?dry_run=false&include_naive=true`,
        {},
        { headers: authHeaders() }
      );
      setLastResult(res.data);
      toast.success(`${res.data.modified_in_db} Termine erfolgreich auf Schweizer Zeit umgestellt.`);
      await runDry();
    } catch (e) {
      toast.error(`Normalisierung fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const toChange = preview?.to_change || 0;

  return (
    <Card data-testid="maintenance-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock size={22} className="text-amber-600" />
          Termin-Zeitzonen normalisieren
        </CardTitle>
        <CardDescription>
          Stellt Termine, die versehentlich in UTC oder ohne Zeitzone gespeichert
          wurden, auf Schweizer Lokalzeit um. Der Zeitpunkt bleibt exakt gleich –
          nur die Darstellung wird korrigiert. Sicher &amp; beliebig wiederholbar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={runDry}
            disabled={checking || running}
            data-testid="maintenance-check-btn"
          >
            <Search size={16} className="mr-2" />
            {checking ? 'Prüfe…' : 'Prüfen (Vorschau)'}
          </Button>
          <Button
            onClick={() => setShowConfirm(true)}
            disabled={!preview || toChange === 0 || checking || running}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="maintenance-run-btn"
          >
            <Wand2 size={16} className="mr-2" />
            {running ? 'Wird ausgeführt…' : `Jetzt normalisieren${toChange ? ` (${toChange})` : ''}`}
          </Button>
        </div>

        {preview && (
          <div
            className="rounded-lg border p-4 bg-gray-50 space-y-3 text-sm"
            data-testid="maintenance-result"
          >
            <div className="flex items-center gap-2 font-medium">
              {toChange === 0 ? (
                <><CheckCircle size={18} className="text-green-600" /> Keine betroffenen Termine gefunden.</>
              ) : (
                <><AlertTriangle size={18} className="text-amber-600" /> {toChange} Termine würden umgestellt.</>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-gray-600">
              <span>Geprüfte Kandidaten:</span><span className="text-right font-mono">{preview.scanned_candidates}</span>
              <span>Aus UTC (…Z / +00:00):</span><span className="text-right font-mono">{preview.utc_start_fixed}</span>
              <span>Ohne Offset (naiv):</span><span className="text-right font-mono">{preview.naive_start_fixed}</span>
            </div>
            {preview.samples?.length > 0 && (
              <div className="mt-2">
                <div className="text-gray-500 mb-1">Beispiele:</div>
                <div className="space-y-1 font-mono text-xs">
                  {preview.samples.slice(0, 5).map((s, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-1">
                      <span className="text-gray-400 line-through">{s.old_start}</span>
                      <span className="text-gray-400">→</span>
                      <span className="text-green-700">{s.new_start}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {lastResult && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800" data-testid="maintenance-last-result">
            <CheckCircle size={18} className="inline mr-2 text-green-600" />
            Letzter Lauf: <strong>{lastResult.modified_in_db}</strong> Termine in der Datenbank aktualisiert.
          </div>
        )}
      </CardContent>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent data-testid="maintenance-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Zeitzonen jetzt normalisieren?</DialogTitle>
            <DialogDescription>
              {toChange} Termine werden auf Schweizer Lokalzeit umgestellt. Der
              tatsächliche Zeitpunkt jedes Termins bleibt unverändert – es wird nur
              die gespeicherte Darstellung korrigiert. Die Aktion ist sicher und
              kann beliebig wiederholt werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} data-testid="maintenance-confirm-cancel">
              Abbrechen
            </Button>
            <Button
              onClick={runReal}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="maintenance-confirm-yes"
            >
              Ja, jetzt normalisieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const CreditAboRepairCard = () => {
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const runDry = async () => {
    setChecking(true);
    setLastResult(null);
    try {
      const res = await axios.post(
        `${ABO_ENDPOINT}?dry_run=true`,
        {},
        { headers: authHeaders() }
      );
      setPreview(res.data);
      if ((res.data?.to_change || 0) === 0) {
        toast.success('Alles korrekt – keine Credit-Abos müssen repariert werden.');
      } else {
        toast.info(`${res.data.to_change} Credit-Abos würden korrigiert.`);
      }
    } catch (e) {
      toast.error(`Prüfung fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    } finally {
      setChecking(false);
    }
  };

  const runReal = async () => {
    setShowConfirm(false);
    setRunning(true);
    try {
      const res = await axios.post(
        `${ABO_ENDPOINT}?dry_run=false`,
        {},
        { headers: authHeaders() }
      );
      setLastResult(res.data);
      toast.success(`${res.data.modified_in_db} Credit-Abos erfolgreich korrigiert.`);
      await runDry();
    } catch (e) {
      toast.error(`Reparatur fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const toChange = preview?.to_change || 0;

  return (
    <Card data-testid="credit-abo-repair-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Ticket size={22} className="text-emerald-600" />
          Credit-Abos reparieren
        </CardTitle>
        <CardDescription>
          Korrigiert Credit-Abos (z. B. „10x Personal FLEX"), die durch einen
          früheren Verkaufs-Fehler mit 0 Restpunkten angelegt wurden (Anzeige
          „0/10" statt „10/10"). Es wird auf{' '}
          <span className="font-mono">Gesamt − bereits genutzte Termine</span>{' '}
          gesetzt – bereits verbrauchte Punkte werden NICHT doppelt gutgeschrieben.
          Sicher &amp; beliebig wiederholbar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={runDry}
            disabled={checking || running}
            data-testid="credit-abo-check-btn"
          >
            <Search size={16} className="mr-2" />
            {checking ? 'Prüfe…' : 'Prüfen (Vorschau)'}
          </Button>
          <Button
            onClick={() => setShowConfirm(true)}
            disabled={!preview || toChange === 0 || checking || running}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="credit-abo-run-btn"
          >
            <Wand2 size={16} className="mr-2" />
            {running ? 'Wird ausgeführt…' : `Jetzt reparieren${toChange ? ` (${toChange})` : ''}`}
          </Button>
        </div>

        {preview && (
          <div
            className="rounded-lg border p-4 bg-gray-50 space-y-3 text-sm"
            data-testid="credit-abo-result"
          >
            <div className="flex items-center gap-2 font-medium">
              {toChange === 0 ? (
                <><CheckCircle size={18} className="text-green-600" /> Keine betroffenen Credit-Abos gefunden.</>
              ) : (
                <><AlertTriangle size={18} className="text-amber-600" /> {toChange} Credit-Abos würden korrigiert.</>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-gray-600">
              <span>Geprüfte Credit-Abos:</span><span className="text-right font-mono">{preview.scanned_candidates}</span>
              <span>Zu korrigieren:</span><span className="text-right font-mono">{preview.to_change}</span>
            </div>
            {preview.samples?.length > 0 && (
              <div className="mt-2">
                <div className="text-gray-500 mb-1">Beispiele:</div>
                <div className="space-y-1 text-xs">
                  {preview.samples.slice(0, 8).map((s, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-700">{s.customer || '—'}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-500">{s.name}</span>
                      <span className="text-gray-400">·</span>
                      <span className="font-mono text-gray-400 line-through">{s.old_remaining}/{s.credits_total}</span>
                      <span className="text-gray-400">→</span>
                      <span className="font-mono text-green-700">{s.new_remaining}/{s.credits_total}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {lastResult && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800" data-testid="credit-abo-last-result">
            <CheckCircle size={18} className="inline mr-2 text-green-600" />
            Letzter Lauf: <strong>{lastResult.modified_in_db}</strong> Credit-Abos in der Datenbank korrigiert.
          </div>
        )}
      </CardContent>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent data-testid="credit-abo-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Credit-Abos jetzt reparieren?</DialogTitle>
            <DialogDescription>
              {toChange} Credit-Abos werden korrigiert. Die Restpunkte werden auf
              „Gesamt − bereits genutzte Termine" gesetzt. Bereits verbrauchte
              Punkte werden nicht doppelt gutgeschrieben. Die Aktion ist sicher
              und kann beliebig wiederholt werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} data-testid="credit-abo-confirm-cancel">
              Abbrechen
            </Button>
            <Button
              onClick={runReal}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="credit-abo-confirm-yes"
            >
              Ja, jetzt reparieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const PointsPackageRepairCard = () => {
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const runDry = async () => {
    setChecking(true);
    setLastResult(null);
    try {
      const res = await axios.post(`${POINTS_ENDPOINT}?dry_run=true`, {}, { headers: authHeaders() });
      setPreview(res.data);
      const n = res.data?.type_b_count || 0;
      if (n === 0) {
        toast.success('Keine Punkte-Pakete müssen übertragen werden.');
      } else {
        toast.info(`${n} Kunden würden Restguthaben ins Punkte-Konto erhalten.`);
      }
    } catch (e) {
      toast.error(`Prüfung fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    } finally {
      setChecking(false);
    }
  };

  const runReal = async () => {
    setShowConfirm(false);
    setRunning(true);
    try {
      const res = await axios.post(`${POINTS_ENDPOINT}?dry_run=false`, {}, { headers: authHeaders() });
      setLastResult(res.data);
      toast.success(`${res.data.type_b_applied} Kunden repariert (${res.data.type_b_total_points} Punkte übertragen).`);
      await runDry();
    } catch (e) {
      toast.error(`Reparatur fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const count = preview?.type_b_count || 0;

  return (
    <Card data-testid="points-package-repair-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Coins size={22} className="text-blue-600" />
          Punkte-Pakete reparieren
        </CardTitle>
        <CardDescription>
          Behebt Punkte-Pakete, die fälschlich als „Abo" abgebucht wurden. Bei
          Kunden mit Punkte-Konto = 0 (z. B. Christiane) wird das im Paket
          „gefangene" Restguthaben ins Punkte-Konto übertragen (sichtbar &amp;
          nutzbar). Kunden mit vorhandenem Guthaben bleiben unverändert (Kulanz).
          Sicher &amp; beliebig wiederholbar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={runDry} disabled={checking || running} data-testid="points-package-check-btn">
            <Search size={16} className="mr-2" />
            {checking ? 'Prüfe…' : 'Prüfen (Vorschau)'}
          </Button>
          <Button
            onClick={() => setShowConfirm(true)}
            disabled={!preview || count === 0 || checking || running}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="points-package-run-btn"
          >
            <Wand2 size={16} className="mr-2" />
            {running ? 'Wird ausgeführt…' : `Jetzt reparieren${count ? ` (${count})` : ''}`}
          </Button>
        </div>

        {preview && (
          <div className="rounded-lg border p-4 bg-gray-50 space-y-3 text-sm" data-testid="points-package-result">
            <div className="flex items-center gap-2 font-medium">
              {count === 0 ? (
                <><CheckCircle size={18} className="text-green-600" /> Keine Übertragungen nötig.</>
              ) : (
                <><AlertTriangle size={18} className="text-amber-600" /> {count} Kunden erhalten Restguthaben ({preview.type_b_total_points} Punkte).</>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-gray-600">
              <span>Geprüfte Punkte-Pakete:</span><span className="text-right font-mono">{preview.packages_scanned}</span>
              <span>Testpakete ignoriert:</span><span className="text-right font-mono">{preview.excluded_big}</span>
              <span>Falsche Felder bereinigen:</span><span className="text-right font-mono">{preview.fields_to_clean}</span>
            </div>
            {preview.samples?.length > 0 && (
              <div className="mt-2">
                <div className="text-gray-500 mb-1">Übertragungen:</div>
                <div className="space-y-1 text-xs">
                  {preview.samples.slice(0, 12).map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="font-medium text-gray-700">{s.customer}</span>
                      <span className="text-gray-400">→ Punkte-Konto</span>
                      <span className="font-mono text-green-700">+{s.amount}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {lastResult && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800" data-testid="points-package-last-result">
            <CheckCircle size={18} className="inline mr-2 text-green-600" />
            Letzter Lauf: <strong>{lastResult.type_b_applied}</strong> Kunden repariert,
            {' '}<strong>{lastResult.type_b_total_points}</strong> Punkte übertragen,
            {' '}{lastResult.fields_to_clean} Paket-Felder bereinigt.
          </div>
        )}
      </CardContent>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent data-testid="points-package-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Punkte-Pakete jetzt reparieren?</DialogTitle>
            <DialogDescription>
              Bei {count} Kunden mit Punkte-Konto = 0 wird das Restguthaben aus dem
              Paket ins Punkte-Konto übertragen ({preview?.type_b_total_points} Punkte
              gesamt). Kunden mit bereits vorhandenem Guthaben bleiben unverändert.
              Die Aktion ist sicher und wiederholbar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} data-testid="points-package-confirm-cancel">
              Abbrechen
            </Button>
            <Button onClick={runReal} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="points-package-confirm-yes">
              Ja, jetzt reparieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const ZeroBilledCard = () => {
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [data, setData] = useState(null);
  const [resetIds, setResetIds] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(ZERO_LIST_ENDPOINT, { headers: authHeaders() });
      setData(res.data);
      setResetIds({});
      if ((res.data?.count || 0) === 0) {
        toast.success('Keine mit 0 abgerechneten Termine gefunden.');
      } else {
        toast.info(`${res.data.count} mit 0 abgerechnete Termine gefunden.`);
      }
    } catch (e) {
      toast.error(`Laden fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetItems = async (items, key) => {
    if (!items.length) return;
    setResetting(true);
    try {
      const res = await axios.post(ZERO_RESET_ENDPOINT, { items }, { headers: authHeaders() });
      const done = { ...resetIds };
      items.forEach((it) => { done[`${it.appointment_id}|${it.customer_id}`] = true; });
      setResetIds(done);
      toast.success(`${res.data.reset} Termin(e) zurückgesetzt. Jetzt „Alle Abrechnen" ausführen, damit die Punkte neu abgezogen werden.`);
    } catch (e) {
      toast.error(`Zurücksetzen fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    } finally {
      setResetting(false);
    }
  };

  const items = data?.items || [];
  const openItems = items.filter((it) => !it.already_reset && it.appointment_exists && !resetIds[`${it.appointment_id}|${it.customer_id}`]);

  return (
    <Card data-testid="zero-billed-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileWarning size={22} className="text-rose-600" />
          Mit 0 abgerechnete Termine
        </CardTitle>
        <CardDescription>
          Zeigt Check-ins, die fälschlich mit <strong>0 Punkten</strong> abgerechnet wurden
          (Buchung an ein Punkte-Paket gekoppelt). Du kannst die Abrechnung einzeln oder
          gesammelt zurücksetzen. Danach einmal <strong>„Alle Abrechnen"</strong> ausführen –
          dann werden die Punkte korrekt vom Punkte-Konto abgezogen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={load} disabled={loading || resetting} data-testid="zero-billed-load-btn">
            <Search size={16} className="mr-2" />
            {loading ? 'Lade…' : 'Termine laden'}
          </Button>
          {openItems.length > 0 && (
            <Button
              onClick={() => resetItems(openItems.map((it) => ({ appointment_id: it.appointment_id, customer_id: it.customer_id })), 'all')}
              disabled={loading || resetting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="zero-billed-reset-all-btn"
            >
              <RotateCcw size={16} className="mr-2" />
              {resetting ? 'Wird zurückgesetzt…' : `Alle zurücksetzen (${openItems.length})`}
            </Button>
          )}
        </div>

        {data && (
          <div className="text-sm text-gray-600" data-testid="zero-billed-summary">
            <strong>{data.count}</strong> betroffene Check-ins
            {data.total_missed_points ? <> · offen: <strong>{data.total_missed_points}</strong> nicht abgezogene Punkte</> : null}
          </div>
        )}

        {items.length > 0 && (
          <div className="rounded-lg border overflow-hidden">
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left text-gray-500">
                    <th className="px-3 py-2 font-medium">Kunde</th>
                    <th className="px-3 py-2 font-medium">Training</th>
                    <th className="px-3 py-2 font-medium">Service</th>
                    <th className="px-3 py-2 font-medium text-right">Soll-Punkte</th>
                    <th className="px-3 py-2 font-medium text-right">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const key = `${it.appointment_id}|${it.customer_id}`;
                    const done = it.already_reset || resetIds[key];
                    return (
                      <tr key={key + i} className="border-t" data-testid={`zero-billed-row-${i}`}>
                        <td className="px-3 py-2 font-medium text-gray-800">{it.customer}</td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{fmtDateTime(it.training_time)}</td>
                        <td className="px-3 py-2 text-gray-600">{it.service}</td>
                        <td className="px-3 py-2 text-right font-mono text-rose-600">{it.intended_points ?? '—'}</td>
                        <td className="px-3 py-2 text-right">
                          {done ? (
                            <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                              <CheckCircle size={14} /> zurückgesetzt
                            </span>
                          ) : !it.appointment_exists ? (
                            <span className="text-gray-400 text-xs">Termin fehlt</span>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={resetting}
                              onClick={() => resetItems([{ appointment_id: it.appointment_id, customer_id: it.customer_id }], key)}
                              data-testid={`zero-billed-reset-btn-${i}`}
                            >
                              <RotateCcw size={13} className="mr-1" /> Zurücksetzen
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MaintenanceSettings = () => (
  <>
    <ZeroBilledCard />
    <PointsPackageRepairCard />
    <CreditAboRepairCard />
    <TimezoneCard />
  </>
);

export default MaintenanceSettings;
